import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { CheckCircle2, Circle, Clock, FileText, ShieldCheck, Receipt, AlertCircle, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { normalizeData, getElapsedDays } from './ConsentsTab';
import { SourceAnnotation } from './SourceAnnotation';
import { computeGpConsentFromWorkstreams } from './ProjectOverview';
const KYC_REQUIREMENTS = ['Certificate of Incorporation', 'Beneficial Ownership Declaration', 'AML/CFT Risk Assessment', 'Source of Funds Documentation', 'FATCA/CRS Self-Certification', 'Tax Residency Certificate', 'Authorized Signatory List', 'Board Resolution'];
const REQUIREMENT_NOTES: Record<string, {
  complete: string;
  pending: string;
  notStarted: string;
}> = {
  'Certificate of Incorporation': {
    complete: 'Verified and on file',
    pending: 'Awaiting certified copy from buyer counsel',
    notStarted: 'Certified copy required'
  },
  'Beneficial Ownership Declaration': {
    complete: 'Annual refresh due Jan 2027',
    pending: 'Awaiting UBO details for holding entity',
    notStarted: 'Form to be circulated by fund admin'
  },
  'AML/CFT Risk Assessment': {
    complete: 'Low risk classification',
    pending: 'Enhanced due diligence in progress',
    notStarted: 'Risk assessment not yet initiated'
  },
  'Source of Funds Documentation': {
    complete: 'Bank statements and audit letter on file',
    pending: 'Awaiting updated bank statements',
    notStarted: 'Source of wealth letter required'
  },
  'FATCA/CRS Self-Certification': {
    complete: 'Form W-8BEN-E on file',
    pending: 'Form W-8BEN-E under review',
    notStarted: 'Form W-8BEN-E required'
  },
  'Tax Residency Certificate': {
    complete: 'TRC valid through Dec 2026',
    pending: 'Awaiting issuance from local tax authority',
    notStarted: 'Certificate to be requested'
  },
  'Authorized Signatory List': {
    complete: 'Board-approved list on file',
    pending: 'Updated list pending board approval',
    notStarted: 'Signatory list not yet provided'
  },
  'Board Resolution': {
    complete: 'Resolution authorizing transfer on file',
    pending: 'Draft resolution under legal review',
    notStarted: 'Resolution required prior to execution'
  }
};
const DUE_DATES_PENDING = ["15 Apr '26", "22 Apr '26", "30 Apr '26", "5 May '26", "12 May '26", "20 May '26", "1 Jun '26", "15 Jun '26"];
const DDQ_CATEGORIES = [{
  category: 'Fund Strategy',
  question: "Describe the fund's investment strategy and target sectors.",
  answer: 'Mid-market buyout focused on North American technology, healthcare, and business services companies with enterprise values of $100M-$500M.'
}, {
  category: 'Team & Governance',
  question: 'Key person provisions and succession planning.',
  answer: 'Key person clause applies to James Morrison and Sarah Lin. Suspension triggered if either departs and not replaced within 120 days.'
}, {
  category: 'ESG',
  question: 'ESG integration in investment process.',
  answer: 'ESG factors assessed during due diligence via proprietary scoring framework. Annual ESG reporting to LPs. Signatory to UN PRI.'
}, {
  category: 'Risk Management',
  question: 'Describe concentration limits and diversification.',
  answer: 'Maximum 15% of committed capital per investment. Sector diversification across minimum 3 sectors. Geographic focus on North America.'
}, {
  category: 'Track Record',
  question: 'Historical performance of prior funds.',
  answer: 'Fund I (2015): 2.1x Gross MOIC, 18% Net IRR. Fund II (2018): 1.8x Gross MOIC, 15% Net IRR. Fully realized.'
}, {
  category: 'Terms & Fees',
  question: 'Management fee and carried interest structure.',
  answer: '2% management fee during investment period, stepping down to 1.5% on invested capital. 20% carried interest with 8% hurdle rate.'
}, {
  category: 'Operations',
  question: 'Fund administrator and auditor details.',
  answer: 'Fund Administrator: State Street. Auditor: PwC. Annual audited financials provided within 90 days of year-end.'
}, {
  category: 'Legal & Compliance',
  question: 'Pending litigation or regulatory actions.',
  answer: 'No pending litigation or regulatory actions against the firm or key principals.'
}, {
  category: 'Tax',
  question: 'UBTI and ECI exposure.',
  answer: 'Fund structured to minimize UBTI and ECI. Historically less than 5% of income classified as UBTI.'
}];
function generateDdqResponses(stage: string, partnershipName: string) {
  const seed = partnershipName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  let answeredCount = 0;
  if (stage === 'Complete' || stage === 'Pending Execution') {
    answeredCount = 9;
  } else if (stage === 'TA Drafting') {
    answeredCount = 5 + seed % 3; // 5-7
  } else if (stage === 'DDQ / Diligence') {
    answeredCount = 2 + seed % 3; // 2-4
  } else {
    answeredCount = 0;
  }
  const baseDate = new Date(2024, 0, 10);
  return DDQ_CATEGORIES.map((cat, index) => {
    const isAnswered = index < answeredCount;
    const daysOffset = (seed + index) % 15;
    const updatedDate = new Date(baseDate.getTime() + daysOffset * 24 * 60 * 60 * 1000);
    const dateStr = updatedDate.toISOString().split('T')[0];
    return {
      ...cat,
      isAnswered,
      updatedAt: dateStr,
      displayAnswer: isAnswered ? cat.answer : stage === 'Not Started' ? 'DDQ not yet shared with GP' : stage === 'DDQ / Diligence' ? 'Awaiting GP response' : 'Response pending'
    };
  });
}
function generateKycRequirements(stage: string, partnershipName: string) {
  // Use partnership name to create a deterministic but varied seed
  const seed = partnershipName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  let completeCount = 0;
  let pendingCount = 0;
  if (stage === 'Complete' || stage === 'Pending Execution') {
    completeCount = 8;
  } else if (stage === 'TA Drafting') {
    completeCount = 4 + seed % 3; // 4-6
    pendingCount = 1 + seed % 2; // 1-2
  } else if (stage === 'DDQ / Diligence') {
    completeCount = 1 + seed % 3; // 1-3
    pendingCount = 2 + seed % 2; // 2-3
  } else {
    // Not Started
    completeCount = 0;
    pendingCount = seed % 2; // 0-1
  }
  // Shuffle requirement order based on seed for variety
  const indices = [0, 1, 2, 3, 4, 5, 6, 7];
  for (let i = indices.length - 1; i > 0; i--) {
    const j = (seed + i * 7) % (i + 1);
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.map((origIdx, position) => {
    const req = KYC_REQUIREMENTS[origIdx];
    let status = 'Not Started';
    if (position < completeCount) status = 'Complete';else if (position < completeCount + pendingCount) status = 'Pending';
    const notes = REQUIREMENT_NOTES[req];
    const dueDateIdx = (seed + position) % DUE_DATES_PENDING.length;
    return {
      id: `req-${origIdx}`,
      name: req,
      status,
      dueDate: status === 'Complete' ? '—' : status === 'Pending' ? DUE_DATES_PENDING[dueDateIdx] : 'TBD',
      notes: status === 'Complete' ? notes?.complete || 'Verified by Fund Admin' : status === 'Pending' ? notes?.pending || 'Under review' : notes?.notStarted || '—'
    };
  });
}
const TA_DETAIL_DATA: Record<string, Record<string, any>> = {
  'Kingston Partners': {
    'Kingston Fund III': {
      seller: 'Thunder Bay Partners LLC',
      buyers: [{
        name: 'Alma Bay LLC',
        amount: '$140,000,000',
        percentage: 70
      }, {
        name: 'Alma Bay II LLC',
        amount: '$60,000,000',
        percentage: 30
      }],
      aivs: 'Crescent Ridge Fund III Parallel L.P., Crescent Ridge Fund III (ERISA) L.P., Crescent Ridge Fund III Blocker Corp.',
      effectiveDate: "1 Apr '25"
    },
    'Kingston Growth Equity Fund IV': {
      seller: 'Thunder Bay Partners LLC',
      buyers: [{
        name: 'Alma Bay LLC',
        amount: '$50,000,000',
        percentage: 100
      }],
      aivs: 'Crescent Ridge Growth IV Parallel L.P.',
      effectiveDate: "1 Apr '25"
    },
    'Kingston Asia-Pacific Fund I': {
      seller: 'Thunder Bay Partners LLC',
      buyers: [{
        name: 'Alma Bay LLC',
        amount: '$30,000,000',
        percentage: 50
      }, {
        name: 'Alma Bay II LLC',
        amount: '$15,000,000',
        percentage: 25
      }, {
        name: 'Alma Bay III LLC',
        amount: '$15,000,000',
        percentage: 25
      }],
      aivs: 'Crescent Ridge Asia I L.P., Crescent Ridge Asia I (Cayman) L.P.',
      effectiveDate: "1 Apr '25"
    }
  },
  EQT: {
    'EQT Infrastructure III Co-Investment (A) SCSp': {
      seller: 'BlackRock',
      buyers: [{
        name: 'Northshore Equity Fund III',
        amount: '$120,000,000',
        percentage: 60
      }, {
        name: 'Northshore Co-Invest A',
        amount: '$80,000,000',
        percentage: 40
      }],
      aivs: 'EQT Infra III Blocker S.A., EQT Infra III Parallel SCSp',
      effectiveDate: "15 May '26"
    }
  },
  'Carlyle Group': {
    'Carlyle Partners VI, L.P.': {
      seller: 'Redwood Capital Partners',
      buyers: [{
        name: 'Northshore Equity Fund III',
        amount: '$200,000,000',
        percentage: 80
      }, {
        name: 'Northshore Partners VI',
        amount: '$50,000,000',
        percentage: 20
      }],
      aivs: 'Carlyle Partners VI AIV L.P., Carlyle Partners VI Blocker L.P.',
      effectiveDate: "30 Jun '26"
    },
    'Carlyle Europe Partners IV, L.P.': {
      seller: 'Redwood Capital Partners',
      buyers: [{
        name: 'Northshore Equity Fund III',
        amount: '$90,000,000',
        percentage: 45
      }, {
        name: 'Northshore Europe A',
        amount: '$60,000,000',
        percentage: 30
      }, {
        name: 'Northshore Europe B',
        amount: '$50,000,000',
        percentage: 25
      }],
      aivs: 'CEP IV AIV S.a.r.l., CEP IV Parallel L.P.',
      effectiveDate: "30 Jun '26"
    }
  },
  'Apollo Global Management': {
    'Apollo Investment Fund IX, L.P.': {
      seller: 'Redwood Capital Partners',
      buyers: [{
        name: 'Northshore Equity Fund III',
        amount: '$300,000,000',
        percentage: 100
      }],
      aivs: 'Apollo IX AIV L.P., Apollo IX (Offshore) L.P.',
      effectiveDate: "30 Jun '26"
    },
    'Apollo Investment Fund VIII, L.P.': {
      seller: 'Redwood Capital Partners',
      buyers: [{
        name: 'Northshore Equity Fund III',
        amount: '$150,000,000',
        percentage: 75
      }, {
        name: 'Northshore Co-Invest VIII',
        amount: '$50,000,000',
        percentage: 25
      }],
      aivs: 'Apollo VIII AIV L.P.',
      effectiveDate: "30 Jun '26"
    }
  }
};
interface GpDetailPageProps {
  project: any;
  gpName: string;
  onBack: () => void;
  onBackToProjects: () => void;
  initialTab?: string;
}
const TABS = ['Transfer Agreement', 'Onboarding', 'Diligence', 'Portfolio', 'Documents'];
const STAGES = ['Not Started', 'DDQ / Diligence', 'TA Drafting', 'Pending Execution', 'Complete'];
export function GpDetailPage({
  project,
  gpName,
  onBack,
  onBackToProjects,
  initialTab
}: GpDetailPageProps) {
  const [activeTab, setActiveTab] = useState(initialTab && TABS.includes(initialTab) ? initialTab : TABS[0]);
  const [expandedOnboarding, setExpandedOnboarding] = useState<Record<string, boolean>>({});
  const allData = useMemo(() => normalizeData(project.name), [project.name]);
  const gpData = useMemo(() => allData.filter(d => d.gp === gpName), [allData, gpName]);
  const [selectedDiligenceId, setSelectedDiligenceId] = useState<string>(gpData[0]?.id || '');
  const [selectedOnboardingId, setSelectedOnboardingId] = useState<string>(gpData[0]?.id || '');
  // Reset when GP changes
  useEffect(() => {
    if (gpData.length > 0) {
      setSelectedDiligenceId(gpData[0].id);
      setSelectedOnboardingId(gpData[0].id);
    }
  }, [gpName]);
  const toggleOnboarding = (id: string) => {
    setExpandedOnboarding(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  // Calculate overall status
  const isAllComplete = gpData.every(d => d.stage === 'Complete');
  const isAnyInProgress = gpData.some(d => d.stage !== 'Not Started' && d.stage !== 'Complete');
  let statusColor = 'bg-gray-100 text-gray-600 border-gray-200';
  let statusDot = 'bg-gray-400';
  let statusLabel = 'Not Started';
  if (isAllComplete) {
    statusColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    statusDot = 'bg-emerald-500';
    statusLabel = 'Complete';
  } else if (isAnyInProgress) {
    statusColor = 'bg-amber-50 text-amber-700 border-amber-200';
    statusDot = 'bg-amber-500';
    statusLabel = 'In Progress';
  }
  // Calculate GP Consent status
  const gpConsent = computeGpConsentFromWorkstreams(project.name, gpName);
  let consentColor = 'bg-gray-100 text-gray-600 border-gray-200';
  let consentDot = 'bg-gray-400';
  let consentLabel = gpConsent.label;
  if (gpConsent.status === 'complete') {
    consentColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    consentDot = 'bg-emerald-500';
  } else if (gpConsent.status === 'at_risk') {
    consentColor = 'bg-rose-50 text-rose-700 border-rose-200';
    consentDot = 'bg-rose-500';
  } else if (gpConsent.status === 'in_progress') {
    consentColor = 'bg-amber-50 text-amber-700 border-amber-200';
    consentDot = 'bg-amber-500';
  }
  // Derive consent summary from actual taStatus data on partnerships
  const consentSummary = useMemo(() => {
    if (gpData.length === 0) return '';
    // Collect non-empty taStatus descriptions
    const statuses = gpData.map(d => d.taStatus).filter(s => s && s.trim().length > 0);
    // If we have real taStatus text, use it directly
    if (statuses.length === 1) {
      return statuses[0];
    }
    if (statuses.length > 1) {
      // Combine unique statuses into a cohesive summary
      const unique = [...new Set(statuses)];
      if (unique.length === 1) return unique[0];
      return unique.join('; ');
    }
    // Fallback: generate from stage data when no taStatus is available
    const total = gpData.length;
    const isAllNotStarted = gpData.every(d => d.stage === 'Not Started');
    const isAllDone = gpData.every(d => d.stage === 'Complete');
    if (isAllDone) {
      return 'Fully executed; closing notice issued; no outstanding TA or consent items.';
    }
    if (isAllNotStarted) {
      return `TA and KYC documentation not yet circulated${total > 1 ? ` across ${total} partnerships` : ''}.`;
    }
    // Mixed stages fallback
    const stageCount: Record<string, number> = {};
    gpData.forEach(d => {
      stageCount[d.stage] = (stageCount[d.stage] || 0) + 1;
    });
    const parts: string[] = [];
    if (stageCount['Complete']) parts.push(`${stageCount['Complete']} fully executed`);
    if (stageCount['Pending Execution']) parts.push(`${stageCount['Pending Execution']} pending execution`);
    if (stageCount['TA Drafting']) parts.push(`${stageCount['TA Drafting']} in TA drafting`);
    if (stageCount['DDQ / Diligence']) parts.push(`${stageCount['DDQ / Diligence']} in DDQ/diligence`);
    if (stageCount['Not Started']) parts.push(`${stageCount['Not Started']} not yet circulated`);
    return `${total} partnerships: ${parts.join('; ')}. GP consent and execution pending.`;
  }, [gpData]);
  // Consent box accent colors
  let consentAccent = 'border-l-gray-300';
  let consentBg = 'bg-gray-50';
  let consentLabelColor = 'text-gray-600';
  if (gpConsent.status === 'complete') {
    consentAccent = 'border-l-emerald-500';
    consentBg = 'bg-emerald-50/50';
    consentLabelColor = 'text-emerald-700';
  } else if (gpConsent.status === 'at_risk') {
    consentAccent = 'border-l-rose-500';
    consentBg = 'bg-rose-50/50';
    consentLabelColor = 'text-rose-700';
  } else if (gpConsent.status === 'in_progress') {
    consentAccent = 'border-l-amber-500';
    consentBg = 'bg-amber-50/50';
    consentLabelColor = 'text-amber-700';
  }
  const lastCommDate = gpData.reduce((latest, current) => {
    if (!latest) return current.lastCommDate;
    if (!current.lastCommDate) return latest;
    return getElapsedDays(current.lastCommDate) < getElapsedDays(latest) ? current.lastCommDate : latest;
  }, '');
  const renderStepper = (currentStage: string) => {
    const currentIndex = STAGES.indexOf(currentStage);
    return <div className="flex items-center w-full mt-6" data-id="element-203">
        {STAGES.map((stage, idx) => {
        const isCompleted = idx < currentIndex || currentStage === 'Complete';
        const isCurrent = idx === currentIndex && currentStage !== 'Complete';
        return <Fragment key={stage} data-id="element-204">
              <div className="flex flex-col items-center relative z-10" data-id="element-205">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${isCompleted ? 'bg-[#458b85] border-[#458b85] text-white' : isCurrent ? 'bg-white border-[#458b85] text-[#458b85]' : 'bg-white border-gray-300 text-gray-300'}`} data-id="element-206">
                  {isCompleted ? <CheckCircle2 className="w-4 h-4" data-id="element-207" /> : <span className="text-[10px] font-bold" data-id="element-208">{idx + 1}</span>}
                </div>
                <span className={`absolute top-8 text-[10px] font-medium whitespace-nowrap ${isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-209">
                  {stage}
                </span>
              </div>
              {idx < STAGES.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-[#458b85]' : 'bg-gray-200'}`} data-id="element-210" />}
            </Fragment>;
      })}
      </div>;
  };
  return <div className="animate-in fade-in duration-300" data-id="element-211">
      {/* Breadcrumbs */}
      <div className="flex items-center text-sm font-medium text-gray-500 mb-6" data-id="element-212">
        <button onClick={onBackToProjects} className="hover:text-gray-900 transition-colors" data-id="element-213">
          Projects
        </button>
        <span className="mx-2 text-gray-300" data-id="element-214">/</span>
        <button onClick={onBack} className="hover:text-gray-900 transition-colors" data-id="element-215">
          {project.name}
        </button>
        <span className="mx-2 text-gray-300" data-id="element-216">/</span>
        <span className="text-gray-900" data-id="element-217">{gpName}</span>
      </div>

      {/* GP Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-8 mb-4 shadow-sm" data-id="element-218">
        <div className="flex justify-between items-start" data-id="element-219">
          <div data-id="element-220">
            <div className="flex items-center gap-4 mb-3" data-id="element-221">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-222">{gpName}</h1>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColor}`} data-id="element-223">
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statusDot}`} data-id="element-224" />
                {statusLabel}
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600" data-id="element-225">
              <div className="flex items-center gap-2" data-id="element-226">
                <FileText className="w-4 h-4 text-gray-400" data-id="element-227" />
                <span className="font-medium text-gray-900" data-id="element-228">
                  {gpData.length}
                </span>{' '}
                partnerships
              </div>
              {lastCommDate && <div className="flex items-center gap-2" data-id="element-229">
                  <Clock className="w-4 h-4 text-gray-400" data-id="element-230" />
                  Last communication:{' '}
                  <span className="font-medium text-gray-900" data-id="element-231">
                    {lastCommDate}
                  </span>
                </div>}
            </div>
          </div>
          <div className="flex gap-3" data-id="element-232">
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-233">
              View Contacts
            </button>
            <button className="px-4 py-2 bg-[#458b85] text-white rounded-md text-sm font-medium hover:bg-[#3a7570] transition-colors" data-id="element-234">
              Log Activity
            </button>
          </div>
        </div>
      </div>

      {/* Consent Status Box */}
      <div className={`border-l-4 ${consentAccent} ${consentBg} rounded-r-lg px-5 py-4 mb-8 flex items-start gap-4`} data-id="element-235">
        <div className="flex-1 min-w-0" data-id="element-236">
          <div className="flex items-center gap-2.5 mb-1" data-id="element-237">
            <ShieldCheck className={`w-4 h-4 flex-shrink-0 ${consentLabelColor}`} data-id="element-238" />
            <span className={`text-sm font-bold ${consentLabelColor}`} data-id="element-239">
              {consentLabel}
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-240">
            <SourceAnnotation sourceType="email" sourceTitle="Re: GP Consent Status" sourceDate="Today" extractedValue={consentSummary} context="Consent Status Summary" data-id="element-241">
              {consentSummary}
            </SourceAnnotation>
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-gray-200 mb-8" data-id="element-242">
        {TABS.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab ? 'border-[#458b85] text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-700'}`} data-id="element-243">
            {tab}
          </button>)}
      </div>

      {/* Tab Content */}
      <div data-id="element-244">
        {activeTab === 'Transfer Agreement' && <div className="space-y-6" data-id="element-245">
            {gpData.length === 0 && <div className="bg-gray-50 rounded-xl p-12 text-center text-gray-500" data-id="element-246">
                <p className="text-base" data-id="element-247">
                  No partnership data available for this GP.
                </p>
              </div>}
            {gpData.length > 0 && <>
                <div className="flex items-center justify-end mb-3" data-id="element-248">
                  <button className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-[#458b85] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm" data-id="element-249">
                    <FileText className="w-4 h-4" data-id="element-250" />
                    View Transfer Agreement
                  </button>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden overflow-x-auto" data-id="element-251">
                  <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-252">
                    <thead data-id="element-253">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-254">
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-100" data-id="element-255">
                          Partnership
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-256">
                          Seller(s)
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-257">
                          Buyer(s)
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-258">
                          Commitment
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-259">
                          AIV(s)
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-260">
                          Effective Date
                        </th>
                        <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-261">
                          Invoices
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y-0" data-id="element-262">
                      {gpData.map(data => {
                  const details = TA_DETAIL_DATA[gpName]?.[data.partnership] || {
                    seller: 'Thunder Bay Partners LLC',
                    buyers: [{
                      name: 'Alma Bay II LLC',
                      amount: '$200,000,000',
                      percentage: 100
                    }],
                    aivs: 'Starlight Energy Opportunities Blocker LLC, Starlight Energy Opportunities Fund (Lux) S.C.Sp.',
                    effectiveDate: "1 Apr '25"
                  };
                  return <Fragment key={data.id} data-id="element-263">
                            {details.buyers.map((buyer: any, index: number) => {
                      const isLastBuyer = index === details.buyers.length - 1;
                      return <tr key={`${data.id}-buyer-${index}`} className={`hover:bg-gray-50 transition-colors ${isLastBuyer ? 'border-b-2 border-gray-200' : 'border-b border-gray-100'}`} data-id="element-264">
                                  {index === 0 && <>
                                      <td rowSpan={details.buyers.length} className="py-4 px-6 text-sm font-medium text-gray-900 border-r border-gray-200 bg-white align-top" data-id="element-265">
                                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-100 text-gray-800 text-sm font-medium border border-gray-200/60" data-id="element-266">
                                          {data.partnership}
                                        </span>
                                      </td>
                                      <td rowSpan={details.buyers.length} className="py-4 px-6 text-sm text-gray-900 align-top" data-id="element-267">
                                        <SourceAnnotation sourceType="document" sourceTitle="Transfer Agreement.pdf" sourceDate="Today" extractedValue={details.seller} context="Seller Entity" data-id="element-268">
                                          {details.seller}
                                        </SourceAnnotation>
                                      </td>
                                    </>}
                                  <td className="py-4 px-6 text-sm text-gray-900 align-top" data-id="element-269">
                                    <SourceAnnotation sourceType="document" sourceTitle="Transfer Agreement.pdf" sourceDate="Today" extractedValue={buyer.name} context="Buyer Entity" data-id="element-270">
                                      {buyer.name}
                                    </SourceAnnotation>
                                  </td>
                                  <td className="py-4 px-6 text-sm text-gray-900 align-top" data-id="element-271">
                                    <SourceAnnotation sourceType="document" sourceTitle="Transfer Agreement.pdf" sourceDate="Today" extractedValue={`${buyer.amount} (${buyer.percentage}%)`} context="Commitment Transferred" data-id="element-272">
                                      <div className="flex items-center gap-2" data-id="element-273">
                                        <span className="font-medium text-gray-900" data-id="element-274">
                                          {buyer.amount}
                                        </span>
                                        {buyer.percentage < 100 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[#458b85]/10 text-[#458b85]" data-id="element-275">
                                            {buyer.percentage}%
                                          </span>}
                                      </div>
                                    </SourceAnnotation>
                                  </td>
                                  {index === 0 && <>
                                      <td rowSpan={details.buyers.length} className="py-4 px-6 text-sm text-gray-600 whitespace-normal min-w-[250px] align-top" data-id="element-276">
                                        <SourceAnnotation sourceType="document" sourceTitle="Transfer Agreement.pdf" sourceDate="Today" extractedValue={details.aivs} context="Alternative Investment Vehicles" data-id="element-277">
                                          <div className="flex flex-col gap-1" data-id="element-278">
                                            {details.aivs.split(',').map((aiv: string, i: number) => <span key={i} className="text-sm text-gray-600 leading-snug" data-id="element-279">
                                                  {aiv.trim()}
                                                </span>)}
                                          </div>
                                        </SourceAnnotation>
                                      </td>
                                      <td rowSpan={details.buyers.length} className="py-4 px-6 text-sm text-gray-900 align-top" data-id="element-280">
                                        <SourceAnnotation sourceType="document" sourceTitle="Transfer Agreement.pdf" sourceDate="Today" extractedValue={details.effectiveDate} context="Effective Date" data-id="element-281">
                                          {details.effectiveDate}
                                        </SourceAnnotation>
                                      </td>
                                      <td rowSpan={details.buyers.length} className="py-4 px-6 text-sm align-top" data-id="element-282">
                                        {data.invoiceAmount ? <div className="flex flex-col gap-1" data-id="element-283">
                                            <span className="font-semibold text-gray-900" data-id="element-284">
                                              {data.invoiceAmount}
                                            </span>
                                            {data.invoiceSplit && <span className="text-xs text-gray-400" data-id="element-285">
                                                {data.invoiceSplit}{' '}
                                                <span className="text-gray-300" data-id="element-286">
                                                  (B/S)
                                                </span>
                                              </span>}
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap mt-1 w-fit ${data.invoice === 'Payment Confirmed / Paid' ? 'bg-emerald-50 text-emerald-700' : data.invoice === 'Invoice Pending Payment' || data.invoice === 'Invoice Circulating / Sent' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-287">
                                              {data.invoice || 'Not Issued'}
                                            </span>
                                          </div> : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${data.invoice === 'Payment Confirmed / Paid' ? 'bg-emerald-50 text-emerald-700' : data.invoice === 'Invoice Pending Payment' || data.invoice === 'Invoice Circulating / Sent' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-288">
                                            {data.invoice || 'Not Issued'}
                                          </span>}
                                      </td>
                                    </>}
                                </tr>;
                    })}
                          </Fragment>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}

        {activeTab === 'Onboarding' && <div className="flex gap-0" data-id="element-289">
            {/* Left Navigation */}
            <div className="w-1/4 border-r border-gray-200 pr-0" data-id="element-290">
              <div className="pr-2" data-id="element-291">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-4 pb-3" data-id="element-292">
                  Partnerships
                </p>
                <div className="flex flex-col" data-id="element-293">
                  {gpData.map(data => {
                const isSelected = selectedOnboardingId === data.id;
                const requirements = generateKycRequirements(data.stage, data.partnership);
                const completeCount = requirements.filter(r => r.status === 'Complete').length;
                const pendingCount = requirements.filter(r => r.status === 'Pending').length;
                const totalCount = requirements.length;
                return <button key={data.id} onClick={() => setSelectedOnboardingId(data.id)} className={`text-left px-4 py-3 border-l-2 transition-all ${isSelected ? 'border-l-[#458b85] bg-gray-50' : 'border-l-transparent hover:bg-gray-50/60'}`} data-id="element-294">
                        <span className="text-sm font-medium text-gray-900 block leading-snug" data-id="element-295">
                          {data.partnership}
                        </span>
                        <div className="flex items-center gap-3 mt-2" data-id="element-296">
                          <div className="flex gap-0.5 flex-1" data-id="element-297">
                            {requirements.map((req, idx) => <div key={idx} className={`h-1.5 flex-1 rounded-full ${req.status === 'Complete' ? 'bg-emerald-500' : req.status === 'Pending' ? 'bg-amber-400' : 'bg-gray-200'}`} data-id="element-298" />)}
                          </div>
                          <span className="text-[11px] font-semibold text-gray-500 whitespace-nowrap" data-id="element-299">
                            {completeCount}/{totalCount}
                          </span>
                        </div>
                      </button>;
              })}
                </div>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="w-3/4 pl-8" data-id="element-300">
              {gpData.filter(d => d.id === selectedOnboardingId).map(data => {
            const requirements = generateKycRequirements(data.stage, data.partnership);
            const completeCount = requirements.filter(r => r.status === 'Complete').length;
            const totalCount = requirements.length;
            return <div key={data.id} data-id="element-301">
                      <div className="flex items-center justify-between mb-6" data-id="element-302">
                        <h3 className="font-bold text-gray-900 text-lg" data-id="element-303">
                          KYC / Onboarding:{' '}
                          <span className="text-[#458b85]" data-id="element-304">
                            {data.partnership}
                          </span>
                        </h3>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-305">
                          {completeCount}/{totalCount} Complete
                        </span>
                      </div>

                      {/* Compliance note */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-5 py-3 mb-6 flex items-start gap-3" data-id="element-306">
                        <ShieldCheck className="w-4 h-4 text-[#458b85] flex-shrink-0 mt-0.5" data-id="element-307" />
                        <div className="text-sm text-gray-600 leading-relaxed" data-id="element-308">
                          Standard KYC pack required for all transferee
                          entities. Fund administrator:{' '}
                          <span className="font-medium text-gray-900" data-id="element-309">
                            State Street
                          </span>{' '}
                          (kyc-team@statestreet.com)
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-310">
                        <table className="w-full text-left border-collapse" data-id="element-311">
                          <thead data-id="element-312">
                            <tr className="border-b border-gray-200 bg-gray-50" data-id="element-313">
                              <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-314">
                                Requirement
                              </th>
                              <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-315">
                                Status
                              </th>
                              <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-316">
                                Due Date
                              </th>
                              <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-317">
                                Notes
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white" data-id="element-318">
                            {requirements.map(req => <tr key={req.id} className="hover:bg-gray-50 transition-colors" data-id="element-319">
                                <td className="py-3 px-6 text-sm font-medium text-gray-900" data-id="element-320">
                                  {req.name}
                                </td>
                                <td className="py-3 px-6" data-id="element-321">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${req.status === 'Complete' ? 'bg-emerald-50 text-emerald-700' : req.status === 'Pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-322">
                                    {req.status}
                                  </span>
                                </td>
                                <td className="py-3 px-6 text-sm text-gray-500" data-id="element-323">
                                  {req.dueDate}
                                </td>
                                <td className="py-3 px-6 text-sm text-gray-500" data-id="element-324">
                                  <SourceAnnotation sourceType="document" sourceTitle="KYC Pack.pdf" sourceDate="Today" extractedValue={req.notes} context="KYC Requirement Notes" data-id="element-325">
                                    {req.notes}
                                  </SourceAnnotation>
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>;
          })}
            </div>
          </div>}

        {activeTab === 'Diligence' && <div className="flex gap-0" data-id="element-326">
            {/* Left Navigation */}
            <div className="w-1/4 border-r border-gray-200 pr-0" data-id="element-327">
              <div className="pr-2" data-id="element-328">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-4 pb-3" data-id="element-329">
                  Partnerships
                </p>
                <div className="flex flex-col" data-id="element-330">
                  {gpData.map(data => {
                const isSelected = selectedDiligenceId === data.id;
                let statusLabel = 'Not Started';
                let statusColor = 'text-gray-400';
                let statusDotColor = 'bg-gray-300';
                if (data.stage === 'Complete' || data.stage === 'Pending Execution') {
                  statusLabel = 'Completed';
                  statusColor = 'text-emerald-600';
                  statusDotColor = 'bg-emerald-500';
                } else if (data.stage === 'TA Drafting') {
                  statusLabel = 'In Progress';
                  statusColor = 'text-amber-600';
                  statusDotColor = 'bg-amber-500';
                } else if (data.stage === 'DDQ / Diligence') {
                  statusLabel = 'Shared with GP';
                  statusColor = 'text-[#458b85]';
                  statusDotColor = 'bg-[#458b85]';
                }
                return <button key={data.id} onClick={() => setSelectedDiligenceId(data.id)} className={`text-left px-4 py-3 border-l-2 transition-all ${isSelected ? 'border-l-[#458b85] bg-gray-50' : 'border-l-transparent hover:bg-gray-50/60'}`} data-id="element-331">
                        <span className="text-sm font-medium text-gray-900 block leading-snug" data-id="element-332">
                          {data.partnership}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium mt-1.5 ${statusColor}`} data-id="element-333">
                          <span className={`w-1.5 h-1.5 rounded-full ${statusDotColor}`} data-id="element-334" />
                          {statusLabel}
                        </span>
                      </button>;
              })}
                </div>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="w-3/4 pl-8" data-id="element-335">
              {gpData.filter(d => d.id === selectedDiligenceId).map(data => {
            const responses = generateDdqResponses(data.stage, data.partnership);
            return <div key={data.id} data-id="element-336">
                      <h3 className="font-bold text-gray-900 text-lg mb-6" data-id="element-337">
                        DDQ Responses:{' '}
                        <span className="text-[#458b85]" data-id="element-338">
                          {data.partnership}
                        </span>
                      </h3>

                      <div className="divide-y divide-gray-100" data-id="element-339">
                        {responses.map((resp, idx) => <div key={idx} className="py-5 first:pt-0" data-id="element-340">
                            <div className="flex justify-between items-start mb-2" data-id="element-341">
                              <span className="text-xs font-semibold text-[#458b85] uppercase tracking-wider" data-id="element-342">
                                {resp.category}
                              </span>
                              <span className="text-xs text-gray-400" data-id="element-343">
                                {resp.updatedAt}
                              </span>
                            </div>

                            <h4 className="text-sm font-semibold text-gray-900 mb-1.5" data-id="element-344">
                              {resp.question}
                            </h4>

                            <p className={`text-sm leading-relaxed ${resp.isAnswered ? 'text-gray-600' : 'text-gray-400 italic'}`} data-id="element-345">
                              {resp.isAnswered ? <SourceAnnotation sourceType="document" sourceTitle="DDQ Response.pdf" sourceDate={resp.updatedAt} extractedValue={resp.displayAnswer} context={`DDQ Answer: ${resp.category}`} data-id="element-346">
                                  {resp.displayAnswer}
                                </SourceAnnotation> : resp.displayAnswer}
                            </p>
                          </div>)}
                      </div>
                    </div>;
          })}
            </div>
          </div>}

        {activeTab === 'Portfolio' && <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center text-gray-500" data-id="element-347">
            <p className="text-base" data-id="element-348">Portfolio content coming soon.</p>
          </div>}

        {activeTab === 'Documents' && <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center text-gray-500" data-id="element-349">
            <p className="text-base" data-id="element-350">Documents content coming soon.</p>
          </div>}
      </div>
    </div>;
}